import React, { Fragment } from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import {
    BulkDeleteButton,
    TextInput,
    Filter,
    ReferenceInput,
    SelectInput,
    AutocompleteInput,
    DateInput,
    downloadCSV,
    TextField,
    DateField,
    ShowButton,
    FunctionField,
    ChipField,
} from 'react-admin';

import jsonExport from 'jsonexport/dist';

import { ChargeType } from '../constants';
import CustomSearchInput from '../components/CustomSearchInput';
import BulkEdit from './components/BulkEdit';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import { formatMoney } from '../core/utils/money';
import ChargeUserField from './components/ChargeUserField';
import ChargePartnerField from './components/ChargePartnerField';
import ChargeCountryField from './components/ChargeCountryField';
import ChargeInvoiceField from './components/ChargeInvoiceField';
import ChargeOrderField from './components/ChargeOrderField';

const exporter = charges => {
    const chargesForExport = charges.map((charge, index) => {
        const chargeForExport: any = {};
        chargeForExport.serial = index + 1; // serial
        chargeForExport.id = charge.id;
        chargeForExport.type = charge.type;
        chargeForExport.amount =
            charge.type === 'credit' ? `-${charge.amount}` : charge.amount;
        chargeForExport.currency = charge.currency;
        chargeForExport.year = charge.year;
        chargeForExport.month = charge.month;
        chargeForExport.currency = charge.currency;
        chargeForExport.startDate = charge.startDate;
        chargeForExport.endDate = charge.endDate;
        chargeForExport.description = charge.description;
        chargeForExport.invoiceId = charge.invoiceId;
        chargeForExport.orderId = charge.orderId;
        chargeForExport.customerName = charge.user.name;
        chargeForExport.customerEmail = charge.user.email;

        return chargeForExport;
    });
    jsonExport(
        chargesForExport,
        {
            //headers: ['serial', 'id', 'type', 'amount', 'currency', 'year', 'month', ], // order fields in the export
        },
        (err, csv) => {
            downloadCSV(csv, 'charges'); // download as 'posts.csv` file
        }
    );
};

const BulkActionButtons = props => (
    <Fragment>
        <BulkEdit {...props} />
        <BulkDeleteButton undoable={false} {...props} />
    </Fragment>
);

const ChargeFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />

        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user =>
                    user && (user.name ? user.name : user.email)
                }
            />
        </ReferenceInput>

        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput />
        </ReferenceInput>

        <SelectInput
            source="type"
            choices={Object.keys(ChargeType).map(c => ({ id: c, name: c }))}
        />

        <SelectInput
            source="invoiceId"
            label="Status"
            choices={[{ id: 'null', name: 'Unassigned' }]}
        />

        <DateInput
            source="createdAt[$gte]"
            label="Date From"
            parse={v => v && moment(v).startOf('day').format()}
        />
        <DateInput
            source="createdAt[$lte]"
            label="Date To"
            parse={v => v && moment(v).endOf('day').format()}
        />
    </Filter>
);

// Default columns configuration
const defaultColumns: ColumnConfig[] = [
    { id: 'serial', label: 'Serial', visible: true, order: 0 },
    { id: 'id', label: 'ID', visible: true, order: 1 },
    { id: 'type', label: 'Type', visible: true, order: 2 },
    { id: 'amount', label: 'Amount', visible: true, order: 3 },
    { id: 'currency', label: 'Currency', visible: true, order: 4 },
    { id: 'description', label: 'Description', visible: true, order: 5 },
    { id: 'customer', label: 'Customer', visible: true, order: 6 },
    { id: 'partner', label: 'Partner', visible: true, order: 7 },
    { id: 'order', label: 'Order', visible: true, order: 8 },
    { id: 'invoice', label: 'Invoice', visible: true, order: 9 },
    { id: 'createdAt', label: 'Created', visible: true, order: 10 },
    { id: 'show', label: 'Actions', visible: true, order: 11 },

    // Optional columns (visible: false by default)
    { id: 'updatedAt', label: 'Updated', visible: false, order: 12 },
    { id: 'deletedAt', label: 'Deleted', visible: false, order: 13 },
    { id: 'year', label: 'Year', visible: false, order: 14 },
    { id: 'month', label: 'Month', visible: false, order: 15 },
    { id: 'startDate', label: 'Start Date', visible: false, order: 16 },
    { id: 'endDate', label: 'End Date', visible: false, order: 17 },
    { id: 'userId', label: 'User ID', visible: false, order: 18 },
    { id: 'storageId', label: 'Storage ID', visible: false, order: 19 },
    { id: 'requestId', label: 'Request ID', visible: false, order: 20 },
    { id: 'invoiceId', label: 'Invoice ID', visible: false, order: 21 },
    { id: 'partnerId', label: 'Partner ID', visible: false, order: 22 },
    { id: 'countryId', label: 'Country ID', visible: false, order: 23 },
    { id: 'serviceId', label: 'Service ID', visible: false, order: 24 },
    { id: 'orderId', label: 'Order ID', visible: false, order: 25 },
    { id: 'revision', label: 'Revision', visible: false, order: 26 },
    { id: 'country', label: 'Country', visible: false, order: 27 },

    // User details from nested user object
    { id: 'userEmail', label: 'Customer Email', visible: false, order: 28 },
    { id: 'userPhone', label: 'Customer Phone', visible: false, order: 29 },

    // Partner details from nested partner object
    { id: 'partnerName', label: 'Partner Name', visible: false, order: 30 },

    // Country details from nested country object
    { id: 'countryName', label: 'Country Name', visible: false, order: 31 },

    // Metadata fields
    { id: 'rent', label: 'Rent', visible: false, order: 32 },
    { id: 'source', label: 'Source', visible: false, order: 33 },
    { id: 'orderRate', label: 'Order Rate', visible: false, order: 34 },
    { id: 'orderRent', label: 'Order Rent', visible: false, order: 35 },
    { id: 'orderAmount', label: 'Order Amount', visible: false, order: 36 },
    {
        id: 'orderCurrentSize',
        label: 'Order Current Size',
        visible: false,
        order: 37,
    },
];

// Function to render columns based on column ID
const renderColumn = (columnId: string) => {
    switch (columnId) {
        case 'serial':
            return (
                <TextField key={columnId} source="serial" sortable={false} />
            );
        case 'id':
            return <TextField key={columnId} source="id" />;
        case 'type':
            return <ChipField key={columnId} source="type" />;
        case 'amount':
            return (
                <FunctionField
                    key={columnId}
                    label="Amount"
                    render={record => {
                        return record.type === 'credit' ? (
                            <>
                                <small style={{ display: 'block' }}>
                                    {record.currency}
                                </small>
                                -{formatMoney(record.amount, record.currency)}
                            </>
                        ) : (
                            <>
                                <small style={{ display: 'block' }}>
                                    {record.currency}
                                </small>
                                {formatMoney(record.amount, record.currency)}
                            </>
                        );
                    }}
                />
            );
        case 'currency':
            return <TextField key={columnId} source="currency" />;
        case 'description':
            return (
                <FunctionField
                    key={columnId}
                    label="Description"
                    render={record => {
                        const desc = record.description || '';
                        if (!desc) return '';
                        const text =
                            desc.length > 50 ? `${desc.slice(0, 50)}...` : desc;
                        return (
                            <Tooltip title={desc} arrow placement="top">
                                <span>{text}</span>
                            </Tooltip>
                        );
                    }}
                />
            );
        case 'customer':
            return (
                <ChargeUserField key={columnId} link="show" label="Customer" />
            );
        case 'partner':
            return (
                <ChargePartnerField
                    key={columnId}
                    link="show"
                    label="Partner"
                />
            );
        case 'order':
            return (
                <ChargeOrderField key={columnId} link="show" label="Order" />
            );
        case 'invoice':
            return (
                <ChargeInvoiceField
                    key={columnId}
                    link="show"
                    label="Invoice"
                />
            );
        case 'createdAt':
            return (
                <DateField
                    key={columnId}
                    locales="en-IN"
                    source="createdAt"
                    showTime
                    label="Created"
                />
            );
        case 'show':
            return (
                <ShowButton key={columnId} label="" style={{ padding: 0 }} />
            );

        // Optional columns
        case 'updatedAt':
            return (
                <DateField
                    key={columnId}
                    locales="en-IN"
                    source="updatedAt"
                    showTime
                    label="Updated"
                />
            );
        case 'deletedAt':
            return (
                <DateField
                    key={columnId}
                    locales="en-IN"
                    source="deletedAt"
                    showTime
                    label="Deleted"
                />
            );
        case 'year':
            return <TextField key={columnId} source="year" />;
        case 'month':
            return <TextField key={columnId} source="month" />;
        case 'startDate':
            return (
                <TextField key={columnId} source="startDate" label="Start" />
            );
        case 'endDate':
            return <TextField key={columnId} source="endDate" label="End" />;
        case 'userId':
            return <TextField key={columnId} source="userId" />;
        case 'storageId':
            return <TextField key={columnId} source="storageId" />;
        case 'requestId':
            return <TextField key={columnId} source="requestId" />;
        case 'invoiceId':
            return <TextField key={columnId} source="invoiceId" />;
        case 'partnerId':
            return <TextField key={columnId} source="partnerId" />;
        case 'countryId':
            return <TextField key={columnId} source="countryId" />;
        case 'serviceId':
            return <TextField key={columnId} source="serviceId" />;
        case 'orderId':
            return <TextField key={columnId} source="orderId" />;
        case 'revision':
            return <TextField key={columnId} source="revision" />;
        case 'country':
            return (
                <ChargeCountryField
                    key={columnId}
                    link="show"
                    label="Country"
                />
            );

        // User details from nested user object
        case 'userEmail':
            return (
                <FunctionField
                    key={columnId}
                    label="Customer Email"
                    render={record => record.user?.email || ''}
                />
            );
        case 'userPhone':
            return (
                <FunctionField
                    key={columnId}
                    label="Customer Phone"
                    render={record => record.user?.phone || ''}
                />
            );

        // Partner details from nested partner object
        case 'partnerName':
            return (
                <FunctionField
                    key={columnId}
                    label="Partner Name"
                    render={record => record.partner?.name || ''}
                />
            );

        // Country details from nested country object
        case 'countryName':
            return (
                <FunctionField
                    key={columnId}
                    label="Country Name"
                    render={record => record.country?.name || ''}
                />
            );

        // Metadata fields
        case 'rent':
            return (
                <FunctionField
                    key={columnId}
                    label="Rent"
                    render={record => {
                        const rent = record.metadata?.rent;
                        return rent
                            ? formatMoney(rent * 1000, record.currency)
                            : '';
                    }}
                />
            );
        case 'source':
            return (
                <FunctionField
                    key={columnId}
                    label="Source"
                    render={record => record.metadata?.source || ''}
                />
            );
        case 'orderRate':
            return (
                <FunctionField
                    key={columnId}
                    label="Order Rate"
                    render={record => record.metadata?.orderRate || ''}
                />
            );
        case 'orderRent':
            return (
                <FunctionField
                    key={columnId}
                    label="Order Rent"
                    render={record => {
                        const orderRent = record.metadata?.orderRent;
                        return orderRent
                            ? formatMoney(orderRent * 1000, record.currency)
                            : '';
                    }}
                />
            );
        case 'orderAmount':
            return (
                <FunctionField
                    key={columnId}
                    label="Order Amount"
                    render={record => {
                        const orderAmount = record.metadata?.orderAmount;
                        return orderAmount
                            ? formatMoney(orderAmount * 1000, record.currency)
                            : '';
                    }}
                />
            );
        case 'orderCurrentSize':
            return (
                <FunctionField
                    key={columnId}
                    label="Order Current Size"
                    render={record => record.metadata?.orderCurrentSize || ''}
                />
            );
        default:
            return null;
    }
};

const ChargeList = props => (
    <CustomizableGrid
        title="Charges"
        defaultColumns={defaultColumns}
        storageKey="chargesList"
        renderColumn={renderColumn}
        filtersComponent={<ChargeFilter />}
        bulkActionButtons={<BulkActionButtons />}
        exporter={exporter}
        sort={{ field: 'id', order: 'DESC' }}
        permissions={props.permissions || []}
        {...props}
    />
);

ChargeList.defaultProps = {
    resource: 'charges',
    location: { search: '' },
};

export default ChargeList;
